import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import EntityMember from "_shared/entity-members/entity-member";
import EntityMemberSchema from "_shared/entity-members/entity-member-schema";
import FeeScheduleCategoryDescription from "_shared/fee-schedules/categories/descriptions/fee-schedule-category-description";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import FeeSchema from "_shared/fee-schedules/categories/fees/fee-schema";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import Guid from "_shared/guids/guid";
import {
  DEFAULT_FEE_SCHEDULE_CATEGORY_TYPE,
  FeeScheduleCategoryType,
} from "_shared/fee-schedules/categories/types/fee-schedule-category-type";
import { Currency } from "_shared/money/currencies/currency";
import { z } from "zod";

export type FeeScheduleCategoryJSON = z.infer<typeof FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ>;
export type FeeScheduleCategoryParams = z.infer<typeof FeeScheduleCategorySchema.FeeScheduleCategoryZ>;

export default class FeeScheduleCategorySchema {
  public static readonly FeeScheduleCategoryJSONZ = z.object({
    id: z.uuid().nullish(),
    name: z.string(),
    description: z.string().nullish(),
    // Nullish only so a stored value from before the type existed still reads; it reads as a fee.
    type: z.enum(FeeScheduleCategoryType).nullish(),
    currencyIsMasked: z.boolean().nullish(),
    feeRate: z.number().nullish(),
    feeCurrency: z.enum(Currency).nullish(),
    feeUnit: z.string().nullish(),
    teamMember: EntityMemberSchema.EntityMemberJSONZ.nullish(),
    teamMemberIsMasked: z.boolean().nullish(),
    version: z.number().nullish(),
  });

  public static readonly FeeScheduleCategoryZ = z.object({
    id: z.instanceof(Guid),
    name: z.instanceof(FeeScheduleCategoryName),
    description: z.instanceof(FeeScheduleCategoryDescription).optional().nullable(),
    fee: z.instanceof(Fee).nullish(),
    // Optional for the callers that build a category in code; the class reads none as a fee.
    type: z.enum(FeeScheduleCategoryType).optional(),
    currencyIsMasked: z.boolean().optional(),
    teamMember: z.instanceof(EntityMember).optional().nullable(),
    teamMemberIsMasked: z.boolean().optional(),
    version: z.number().optional(),
  });

  public static validate(values: unknown): FeeScheduleCategoryParams {
    return parseOrThrow(FeeScheduleCategorySchema.FeeScheduleCategoryZ, values, {
      logError: true,
      schemaName: "FeeScheduleCategorySchema",
      operation: "Validating FeeScheduleCategoryParams",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): FeeScheduleCategoryJSON {
    return parseOrThrow(FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ, json, {
      logError: true,
      schemaName: "FeeScheduleCategorySchema",
      operation: "Validating FeeScheduleCategoryJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is FeeScheduleCategoryParams {
    try {
      FeeScheduleCategorySchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is FeeScheduleCategoryJSON {
    try {
      FeeScheduleCategorySchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: FeeScheduleCategoryJSON): FeeScheduleCategoryParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ, json, {
      logError: true,
      schemaName: "FeeScheduleCategorySchema",
      operation: "Deserializing FeeScheduleCategoryJSON",
      payloadSample: json,
    });
    return FeeScheduleCategorySchema.codec.decode(parsed);
  }

  public static serialize(category: FeeScheduleCategoryParams): FeeScheduleCategoryJSON {
    if (isNullOrUndefined(category)) return category;
    const parsed = FeeScheduleCategorySchema.validate(category);
    if (isNullOrUndefined(parsed)) return parsed;
    return FeeScheduleCategorySchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ,
    FeeScheduleCategorySchema.FeeScheduleCategoryZ,
    {
      decode: (data) => {
        // A fee is a rate, or `null` for one the other side sets (#6627). A category with no rate is
        // deferred whatever currency or unit the server left beside it: reading those shapes as
        // `undefined` made the editor treat a deferred fee as "not deferred". Only a masked currency
        // has no fee this reader can know, and a rate with no currency is not a fee at all.
        let feeValue: Fee | null | undefined;
        if (data.currencyIsMasked) {
          feeValue = undefined;
        } else if (isNullOrUndefined(data.feeRate)) {
          feeValue = null;
        } else if (data.feeCurrency) {
          feeValue = Fee.create(
            FeeSchema.deserialize({
              rate: {
                amount: data.feeRate,
                currency: data.feeCurrency,
              },
              unit: data.feeUnit,
            }),
          );
        }
        return {
          id: Guid.fromJSON(data.id) ?? Guid.generate(),
          name: new FeeScheduleCategoryName(data.name),
          description: data.description ? new FeeScheduleCategoryDescription(data.description) : undefined,
          fee: feeValue,
          type: data.type ?? DEFAULT_FEE_SCHEDULE_CATEGORY_TYPE,
          currencyIsMasked: data.currencyIsMasked ?? false,
          teamMember: EntityMember.fromJSON(data.teamMember),
          teamMemberIsMasked: data.teamMemberIsMasked ?? false,
          version: data.version ?? undefined,
        };
      },
      encode: (category) => ({
        id: category.id?.toJSON(),
        name: category.name.value,
        description: category.description?.value,
        feeRate: category.fee === null ? null : category.fee?.rate.amount,
        feeCurrency: category.fee === null ? null : category.fee?.rate.currency,
        feeUnit: category.fee === null ? null : category.fee?.unit ?? undefined,
        type: category.type ?? DEFAULT_FEE_SCHEDULE_CATEGORY_TYPE,
        currencyIsMasked: category.currencyIsMasked,
        teamMember: category.teamMember?.toJSON(),
        teamMemberIsMasked: category.teamMemberIsMasked,
        version: category.version,
      }),
    },
  );
}
